import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Card,
  Stack,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFSelect, RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid whitesmoke',
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
};

const REJECTION_REASONS = [
    { value: '', label: '-- Select Reason --', prefill: '' },
    { value: 'invalid_documents', label: 'Invalid Documents', prefill: 'The documents provided are invalid or do not meet our requirements.' },
    { value: 'mismatched_information', label: 'Mismatched Information', prefill: 'The information provided does not match the submitted documents.' },
    { value: 'fraud_suspected', label: 'Fraud Suspected', prefill: 'There is suspicion of fraudulent activity based on the submitted information.' },
    { value: 'blurred_or_unclear_documents', label: 'Blurred or Unclear Documents', prefill: 'The documents submitted are blurred, unclear, or difficult to read.' },
    { value: 'edited_or_tampered_documents', label: 'Edited or Tampered Documents', prefill: 'The documents appear to have been edited or tampered with and fail validation checks.' },
    { value: 'expired_documents', label: 'Expired Documents', prefill: 'The documents submitted are expired and no longer valid.' },
    { value: 'insufficient_document_quality', label: 'Insufficient Document Quality', prefill: 'The quality of the submitted documents is insufficient for verification purposes.' },
    { value: 'image_format_not_supported', label: 'Unsupported Document Format', prefill: 'The format of the submitted documents is not supported. Please submit images in the correct format.' },
    { value: 'photo_not_matching_id', label: 'Photo Does Not Match ID', prefill: 'The photograph submitted does not match the identification documents provided.' },
    { value: 'incomplete_submission', label: 'Incomplete Submission', prefill: 'Required documents or information have not been fully provided.' },
    { value: 'other', label: 'Other Reason', prefill: '' },
  ];
  

const NewUserSchema = Yup.object().shape({
  action: Yup.string().required('Action is required'),
  rejection_reason: Yup.string().when('action', {
    is: 'Reject',
    then: Yup.string().required('Please select a rejection reason'),
    otherwise: Yup.string().nullable(),
  }),
  remark: Yup.string().when(['action', 'rejection_reason'], {
    // eslint-disable-next-line camelcase
    is: (action, rejection_reason) => action === 'Reject' && rejection_reason === 'other',
    then: Yup.string().required('Remark is required for "Other Reason"'),
    otherwise: Yup.string(),
  }),
  password: Yup.string().required('Password is required'),
});

ReviewKYCModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  submissionID: PropTypes.string.isRequired,
  endPoint: PropTypes.string.isRequired,
};

export default function ReviewKYCModal({ open, handleClose, handleComplete, user, submissionID, endPoint, }) {
  const { enqueueSnackbar } = useSnackbar();
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      action: '',
      rejection_reason: '',
      remark: '',
      password: '',
    },
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  // Prefill remark when rejection reason changes (except 'other')
  useEffect(() => {
    if (values.action === 'Reject') {
      const reasonObj = REJECTION_REASONS.find(r => r.value === values.rejection_reason);
      if (reasonObj && reasonObj.prefill && values.rejection_reason !== 'other') {
        setValue('remark', reasonObj.prefill, { shouldValidate: true });
      } else if (values.rejection_reason === 'other') {
        setValue('remark', '', { shouldValidate: true });
      }
    } else {
      // Clear rejection fields if action is not Reject
      setValue('rejection_reason', '');
      setValue('remark', '');
    }
  }, [values.rejection_reason, values.action, setValue]);

  // Handle delayed submission with countdown
  const onSubmit = () => {
    setCountdown(10); // 8 seconds countdown

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
        //   submitForm();
        methods.handleSubmit(submitForm)();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const submitForm = async () => {
    try {
      const payload = {
        user_id: user.user_id,
        action: values.action,
        rejection_reason: values.action === 'Reject' ? values.rejection_reason : null,
        remark: values.action === 'Reject' ? values.remark : null,
        password: values.password,
        submission_id: submissionID
      };

      // used for different endpoint
      await axiosInstance.post(endPoint, payload);
      enqueueSnackbar('Action completed successfully!');
      reset();
      handleComplete(values.action);
      setCountdown(0);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      setCountdown(0);
    }
  };

  const cancelSubmission = () => {
    clearInterval(countdownRef.current);
    setCountdown(0);
    enqueueSnackbar('Submission cancelled.', { variant: 'info' });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (countdown > 0) {
          enqueueSnackbar('Please cancel the submission countdown before closing.', { variant: 'warning' });
          return;
        }
        reset();
        handleClose();
      }}
      aria-labelledby="kyc-review-modal-title"
      aria-describedby="kyc-review-modal-description"
      closeAfterTransition
      hideBackdrop={false}
    >
      <Box sx={style}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ p: 3 }}>
            <Typography id="kyc-review-modal-title" variant="h6" fontWeight={700} mb={2}>
              Review Account KYC
            </Typography>

            <Stack spacing={3}>
              {/* Action Type */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Action Type
                </Typography>
                <RHFSelect name="action" fullWidth>
                  <option value="">Select option</option>
                  <option value="Approve">Approve</option>
                  <option value="Reject">Reject</option>
                </RHFSelect>
              </Box>

              {/* Rejection Reason */}
              {values.action === 'Reject' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Rejection Reason
                  </Typography>
                  <RHFSelect name="rejection_reason" fullWidth>
                    {REJECTION_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </RHFSelect>
                </Box>
              )}

              {/* Remark */}
              {values.action === 'Reject' && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Remark
                  </Typography>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Write a short reason for rejection to be sent to the user.
                  </Typography>
                  <RHFTextField
                    name="remark"
                    multiline
                    minRows={3}
                    maxRows={6}
                    required={values.action === 'Reject'}
                  />
                </Box>
              )}

              {/* Password */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                  Confirm Password
                </Typography>
                <Typography variant="caption" color="error" gutterBottom>
                  <strong>Warning:</strong> Please ensure full compliance before approving the account. You will be held liable if a bad actor is approved with false information.
                </Typography>
                <RHFTextField name="password" label="Password" type="password" required />
              </Box>

              {/* Countdown and Cancel */}
              {countdown > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Submitting in {countdown} second{countdown !== 1 ? 's' : ''}...{' '}
                  <Button color="inherit" size="small" onClick={cancelSubmission}>
                    Cancel
                  </Button>
                </Alert>
              )}

              {/* Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (countdown > 0) {
                      enqueueSnackbar('Please cancel the submission countdown before closing.', { variant: 'warning' });
                      return;
                    }
                    reset();
                    handleClose();
                  }}
                  size="large"
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting || countdown > 0}
                  disabled={countdown > 0}
                >
                  Submit Review
                </LoadingButton>
              </Stack>
            </Stack>
          </Card>
        </FormProvider>
      </Box>
    </Modal>
  );
}
